import { axios } from '@getgreenline/homi-shared'
import { message } from 'antd'
import React, { createContext, useState } from 'react'
import Endpoints from '../../../../constants/Endpoints'
import { parseErrorMsg } from '../../../../utilities/helpers'
import { IDigitalSignageDevice } from './DevicePage'
import { DigitalSignageAPIState } from './enums'
import { ISignageContract } from './SignagePage'

type DigitalSignageContextType = {
  signages: ISignageContract[]
  devices: IDigitalSignageDevice[]
  getSignages: (companyId: string) => void
  getDevices: (companyId: string) => void
  addSignage: (signage: ISignageContract) => void
  addDevice: (deviceToAdd: IDigitalSignageDevice) => void
  removeDevice: (tenantId: string, retailerId: string, deviceId: string) => void
  removeSignage: (tenantId: string, signageId: string) => void
  status: DigitalSignageAPIState
}

export const DigitalSignageContext = createContext<DigitalSignageContextType>(
  {} as DigitalSignageContextType,
)

export const DigitalSignageProvider = ({ children }: { children: React.ReactElement }) => {
  const [signages, setSignages] = useState<ISignageContract[]>([])
  const [devices, setDevices] = useState<IDigitalSignageDevice[]>([])
  const [status, setStatus] = useState<DigitalSignageAPIState>(DigitalSignageAPIState.DEFAULT)
  const getSignages = async (companyId: string) => {
    setStatus(DigitalSignageAPIState.FETCHING_SIGNAGES)
    const { data } = await axios.get<ISignageContract[]>(Endpoints.GET_SIGNAGES(companyId))
    setSignages(data)
    setStatus(DigitalSignageAPIState.DEFAULT)
  }

  const addSignage = (signageToAdd: ISignageContract) => {
    const signagePosition = signages.findIndex((signage) => signage.id === signageToAdd.id)

    if (signagePosition === -1) {
      setSignages([...signages, signageToAdd])
      return
    }

    const updatedSignages = signages.map((signage) =>
      signage.id === signageToAdd.id ? signageToAdd : signage,
    )

    setSignages([...updatedSignages])
  }

  const getDevices = async (companyId: string) => {
    setStatus(DigitalSignageAPIState.FETCHING_DEVICES)
    const { data } = await axios.get<IDigitalSignageDevice[]>(
      Endpoints.GET_DIGITAL_SIGNAGE_DEVICES(companyId),
    )

    setDevices(data)
    setStatus(DigitalSignageAPIState.DEFAULT)
  }

  const addDevice = (deviceToAdd: IDigitalSignageDevice) => {
    const devicePosition = devices.findIndex((dev) => dev.deviceId === deviceToAdd.deviceId)
    if (devicePosition === -1) {
      setDevices([...devices, deviceToAdd])
      return
    }
    const updatedDevices = devices.map((dev) =>
      dev.deviceId === deviceToAdd.deviceId ? deviceToAdd : dev,
    )
    setDevices([...updatedDevices])
  }

  const removeDevice = async (tenantId: string, retailerId: string, deviceId: string) => {
    setStatus(DigitalSignageAPIState.DELETING_DEVICE)
    try {
      await axios.delete(Endpoints.DELETE_DIGITAL_SIGNAGE_DEVICE(tenantId, retailerId, deviceId))
      setDevices(devices.filter((device) => device.deviceId !== deviceId))
      setStatus(DigitalSignageAPIState.DEFAULT)
    } catch (error) {
      message.error(parseErrorMsg(error))
      setStatus(DigitalSignageAPIState.DEFAULT)
    }
  }

  const removeSignage = async (tenantId: string, signageId: string) => {
    setStatus(DigitalSignageAPIState.DELETING_SIGNAGE)
    try {
      await axios.delete(Endpoints.DELETE_SIGNAGE(tenantId, signageId))
      setSignages(signages.filter((signage) => signage.id !== signageId))
      setStatus(DigitalSignageAPIState.DEFAULT)
    } catch (error) {
      message.error(parseErrorMsg(error))
      setStatus(DigitalSignageAPIState.DEFAULT)
    }
  }

  return (
    <DigitalSignageContext.Provider
      value={{
        signages,
        getSignages,
        addSignage,
        status,
        getDevices,
        devices,
        addDevice,
        removeDevice,
        removeSignage,
      }}
    >
      {children}
    </DigitalSignageContext.Provider>
  )
}
